<script lang="ts" setup>
import { computed, ref, onMounted, onBeforeUnmount } from 'vue';

import { useDialogListener, useNotify } from '@/composables';
import { useYardTaskAnalytics } from '@/analytics/yardTaskEvents';
import { YardTaskFlags } from '@nova/core';
import { removeTag } from '@satellite/helpers/yardTaskUtils';
import { useYardTaskMutations } from '@satellite/composables/useYardTaskMutations';

import type { CheckTaskFormData, MoveTaskFormData, TaskViewData } from '@nova/core';

const {
  isDialogOpen,
  dialogProps,
  closeDialog,
  resetDialogProps,
  subscribeToDialogEvent,
  unsubscribeFromDialogEvent
} = useDialogListener('open-clear-issue-dialog');

const { notify } = useNotify();
const { updateTask: updateTaskMutation } = useYardTaskMutations();
const { trackYardTaskUnflaggedIssue } = useYardTaskAnalytics();
const isLoading = ref(false);

const currentTags = computed(() => {
  if (Array.isArray(dialogProps.value?.tags)) {
    return dialogProps.value.tags;
  }

  return [];
});

const handleConfirm = async () => {
  if (!dialogProps.value?.taskId) {
    console.error('Failed to clear issue: No task ID found');
    notify('Failed to clear issue: Missing task information', 'error');
    return;
  }

  isLoading.value = true;

  const taskId = dialogProps.value.taskId;

  try {
    const updatedTags = removeTag(currentTags.value, YardTaskFlags.ISSUE);
    const formData: Partial<CheckTaskFormData> | Partial<MoveTaskFormData> = {
      tags: updatedTags
    };

    await updateTaskMutation(taskId, formData);

    notify('Issue cleared successfully', 'success');

    const taskType = dialogProps.value.type === 'move' ? 'move' : 'check';
    const taskObject: Partial<TaskViewData> = {
      id: dialogProps.value.taskId,
      type: taskType,
      tags: updatedTags
    };
    trackYardTaskUnflaggedIssue(taskObject);

    try {
      dialogProps.value?.onIssueCleared?.();
    } catch (callbackError) {
      console.error('Error in onIssueCleared callback:', callbackError);
    }

    closeDialog();
  } catch (error) {
    console.error('Failed to clear issue for task', error);
    notify('Failed to clear issue', 'error');
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  subscribeToDialogEvent();
});

onBeforeUnmount(() => {
  unsubscribeFromDialogEvent();
});
</script>

<template>
  <v-dialog
    class="clear-issue-dialog"
    v-model="isDialogOpen"
    @keydown.escape="closeDialog"
    @after-leave="resetDialogProps"
    aria-labelledby="clear-issue-title"
    transition="dialog-bottom-transition"
    fullscreen
    style="top: 25%">
    <v-card>
      <v-card-text class="py-6 px-5 d-flex flex-column">
        <div class="d-flex flex-column align-center justify-center gap-3 my-auto">
          <v-icon icon="mdi-alert-circle" color="#9E9E9E" size="24"></v-icon>
          <h2
            id="clear-issue-title"
            class="typography-heading-md-bold typography-md-heading-lg-bold">
            Has issue been resolved?
          </h2>

          <p class="typography-body-md text-center text--color-text-secondary">
            This task will no longer be marked as having an issue.
          </p>
        </div>
      </v-card-text>

      <v-card-actions class="d-flex flex-column gap-3 gap-md-4 align-stretch flex-md-row pa-4">
        <v-btn
          variant="elevated"
          color="primary"
          size="large"
          class="flex-fill typography-button-md order-1 order-md-2"
          :disabled="isLoading"
          @click="handleConfirm"
          :loading="isLoading">
          Confirm
        </v-btn>
        <v-btn
          variant="outlined"
          size="large"
          class="flex-fill typography-button-md order-2 order-md-1"
          @click="closeDialog"
          :disabled="isLoading">
          Nevermind
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
